import React from 'react';
import {
    ButtonBase,
    Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import {ExpandLess, ExpandMore} from '@material-ui/icons';
import {curry} from 'ramda';
import {
    ApTimelineGroup,
    ApTimelineMarker,
    ApTimelineRow,
} from '@accurate-player/accurate-player-components-external';
import {ButtonClickEvent} from 'VideoAudioQC/Timeline/TimelineTypes';
import {MutedState} from '@accurate-player/accurate-player-plugins';
import {OnClickEvent} from '@pluto/@types';
import QCTooltip from '@pluto/qc-tooltip';
import {FrameRate} from '@accurate-player/accurate-player-core';

import {SetCollapsedAction} from './AudioTrackContainer';
import AudioChannel from '../AudioChannel/AudioChannel';
import {
    AudioChannelData,
    AudioTrackClick,
    AudioTrackData,
} from '../AudioTracksTypes';
import WaveformContainer from '../Waveform/WaveformContainer';
import {getButtonIcon} from '../AudioTracksUtils';

import './AudioTrack.scss';

ApTimelineGroup.register();
ApTimelineRow.register();
ApTimelineMarker.register();

export type ToggleMuteChannelCurried = {
    (channel: number): () => void;
    (): () => void;
}

export interface AudioTrackProps {
    onlyMonoAudioAvailable: boolean;
    trackData: AudioTrackData;
    collapsed: boolean;
    frameRate: FrameRate;
    onToggleCollapse: SetCollapsedAction;
    onToggleMute: (event: ButtonClickEvent) => void;
    onToggleMuteChannel: ToggleMuteChannelCurried;
    onAudioTrackClick: AudioTrackClick;
    setAudioTrackRef: (node: any) => void;
    onChannelRowClick: OnClickEvent;
}

export const getChannelSamples = (index: number, channelWaveforms?: number[][]) => {
    if (!channelWaveforms)
        return [];
    
    const {length} = channelWaveforms;
    
    if (length <= index) {
        return [];
    }
    
    return channelWaveforms[index];
};

export type GetAudioChannelProps = {
    onlyMonoAudioAvailable: boolean,
    src: string,
    frameRate: FrameRate;
    onToggleMuteChannel: ToggleMuteChannelCurried,
    onChannelRowClick: OnClickEvent,
};

const getAudioChannel = curry((props: GetAudioChannelProps, data: AudioChannelData, index: number) => {
    const {
        onlyMonoAudioAvailable,
        src,
        frameRate,
        onChannelRowClick,
        onToggleMuteChannel,
    } = props;
    const {
        label,
        waveformUrl,
    } = data;
    
    const handleToggleChannel = onToggleMuteChannel(index);
    const enabled = data.mutedState === MutedState.Playing;
    
    return (
        <AudioChannel
            key={src + index}
            onlyMonoAudioAvailable={onlyMonoAudioAvailable}
            label={label}
            enabled={enabled}
            frameRate={frameRate}
            waveformUrl={waveformUrl}
            onToggle={handleToggleChannel}
            onRowClick={onChannelRowClick}
        />
    );
});

type GetAudioChannelsProps = {
    trackData: AudioTrackData,
    onlyMonoAudioAvailable: boolean,
    frameRate: FrameRate,
    onToggleMuteChannel: ToggleMuteChannelCurried,
    onChannelRowClick: OnClickEvent,
};

const getAudioChannels = (props: GetAudioChannelsProps) => {
    const {
        trackData,
        onlyMonoAudioAvailable,
        frameRate,
        onToggleMuteChannel,
        onChannelRowClick,
    } = props;
    const {src} = trackData;
    
    const handleChannelMap = getAudioChannel({
        src,
        onlyMonoAudioAvailable,
        frameRate,
        onToggleMuteChannel,
        onChannelRowClick,
    });
    
    return trackData.channelData.map(handleChannelMap);
};

const getCollapseIcon = (props: AudioTrackProps) => {
    const {collapsed} = props;
    
    if (collapsed)
        return (<ExpandMore className="expand-icon" />);
    
    return (<ExpandLess className="expand-icon" />);
};

export function getEnabledChannels(onlyMonoAudioAvailable: boolean, channelsData: AudioChannelData[]) {
    const enabledChannels = channelsData.filter(isPlaying).length;
    
    if (onlyMonoAudioAvailable)
        return (
            <Typography
                variant="body2"
                color="textPrimary"
                className="channels-enabled mono-label"
            >
                Mono
            </Typography>
        );
    
    if (enabledChannels !== 0) {
        return (
            <Typography
                variant="body2"
                color="textSecondary"
                className="channels-enabled"
            >
                {enabledChannels}/{channelsData.length}
            </Typography>
        );
    }
    return (
        <Typography
            variant="body2"
            color="textSecondary"
            className="channels-enabled"
        >
            {}
        </Typography>
    );
}

export function isPlaying(channelData: AudioChannelData) {
    return channelData.mutedState === MutedState.Playing;
}

export default function AudioTrack(props: AudioTrackProps) {
    const {
        trackData,
        onlyMonoAudioAvailable,
        frameRate,
        onToggleCollapse,
        onToggleMute,
        setAudioTrackRef,
        onToggleMuteChannel,
        onChannelRowClick,
    } = props;
    const {
        label,
        waveformUrl,
    } = trackData;
    const isTrackEnabled = onlyMonoAudioAvailable ? trackData.enabled : trackData.channelData.filter(isPlaying).length !== 0;
    const audioChannels = getAudioChannels({
        trackData,
        onlyMonoAudioAvailable,
        frameRate,
        onToggleMuteChannel,
        onChannelRowClick,
    });
    const buttonIcon = getButtonIcon(isTrackEnabled);
    const collapseIcon = getCollapseIcon(props);
    const enabledChannels = getEnabledChannels(onlyMonoAudioAvailable, trackData.channelData);
    
    return (
        <ap-timeline-group
            ref={setAudioTrackRef}
            class="track-group"
            onClick={onToggleCollapse}
        >
            <span
                slot="header"
                className={classNames('track-header', {
                    disabled: !isTrackEnabled,
                })}
            >
                <Typography
                    variant="body2"
                    color="textSecondary"
                    className="track-header__label"
                >
                    {label}
                </Typography>
                {collapseIcon}
                {enabledChannels}
                <QCTooltip
                    title="Toggle Audio Track on/off"
                    arrow
                >
                    <ButtonBase
                        onClick={onToggleMute}
                        aria-label="Toggle Audio Track on/off"
                        className="mute-button"
                    >
                        {buttonIcon}
                    </ButtonBase>
                </QCTooltip>
            </span>
            <span
                slot="content"
            >
                <WaveformContainer
                    enabled={isTrackEnabled}
                    url={waveformUrl}
                    frameRate={frameRate}
                />
            </span>
            {audioChannels}
        </ap-timeline-group>
    );
}
